import * as React from 'react';
import {useRef} from 'react';
import styled from 'styled-components';
import {Button, EMPHASIS, Icon, SubHeading, Table, Text} from '@forecast-it/design-system';
import {EmptyState} from './EmptyState';
import {formatDateFull} from '../utils/formatDate';
import {ticketingAttachmentDownloadUrl} from '../../../graphql/forecastRelay';

interface TicketAttachment {
	id: string;
	filename: string;
	contentType: string;
	sizeBytes: number;
	downloadUrl: string;
	createdAt: string;
	uploadedByName?: string | null;
}

interface Props {
	attachments: readonly TicketAttachment[];
	onUploadFile?: (file: File) => void;
}

function formatBytes(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
	if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
	return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

/**
 * Save a file under its own name.
 *
 * The design system's button column gives a plain `onClick`, not an anchor, so the
 * `download` attribute that makes the browser keep the filename has to be applied to
 * a link created for the purpose. Navigating to the URL instead would leave the saved
 * file named after the proxy route.
 */
function saveFile(url: string, filename: string): void {
	const link = document.createElement('a');
	link.href = url;
	link.download = filename;
	link.rel = 'noopener';
	document.body.appendChild(link);
	link.click();
	link.remove();
}

const Section = styled.section`
	display: flex;
	flex-direction: column;
	gap: ${p => p.theme.sys.spacing[300]};
`;
const Header = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: ${p => p.theme.sys.spacing[300]};
`;
const HiddenInput = styled.input`
	display: none;
`;
const EmptyBody = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: ${p => p.theme.sys.spacing[300]};
`;

interface AttachmentRow {
	id: string;
	filename: string;
	size: string;
	uploadedBy: string;
	when: string;
	downloadUrl: string;
}

/**
 * Every file on the ticket, in one table.
 *
 * The design system's `Table` owns the header type, row height, hover fill and
 * alignment; this supplies the column set and the cell content. The paperclip on the
 * name column stands in for a per-type glyph — the set has one paperclip, and
 * inventing a glyph per file extension would mean inventing colours too.
 */
export function TicketAttachmentsTab({attachments, onUploadFile}: Props) {
	const fileInputRef = useRef<HTMLInputElement>(null);

	const handleUploadClick = () => {
		fileInputRef.current?.click();
	};

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (file && onUploadFile) {
			onUploadFile(file);
		}
		event.target.value = '';
	};

	const rows: AttachmentRow[] = React.useMemo(
		() =>
			attachments.map(a => ({
				id: a.id,
				filename: a.filename,
				size: formatBytes(a.sizeBytes),
				uploadedBy: a.uploadedByName ?? '—',
				when: formatDateFull(a.createdAt),
				downloadUrl: ticketingAttachmentDownloadUrl(a.downloadUrl),
			})),
		[attachments]
	);

	const isEmpty = rows.length === 0;

	return (
		<Section data-testid="attachments-tab">
			<HiddenInput ref={fileInputRef} type="file" onChange={handleFileChange} data-testid="attachments-file-input" />

			<Header>
				<SubHeading data-testid="attachments-count">Attachments</SubHeading>
				{!isEmpty && (
					<Button emphasis={EMPHASIS.MONO} icon="upload" onPress={handleUploadClick} data-cy="attachments-upload">
						Upload file
					</Button>
				)}
			</Header>

			{isEmpty ? (
				<EmptyState data-testid="attachments-empty-state">
					<EmptyBody>
						<Text color="auxiliary">No files attached yet</Text>
						<Button
							emphasis={EMPHASIS.MEDIUM}
							icon="upload"
							onPress={handleUploadClick}
							data-cy="attachments-upload-empty"
						>
							Upload file
						</Button>
					</EmptyBody>
				</EmptyState>
			) : (
				<Table data={rows} getRowId={row => row.id} width="100%" data-cy="attachments-table">
					<Table.TextColumn accessorKey="filename" header="Name" icon={() => <Icon icon="paperclip" size="s" />} />
					<Table.TextColumn accessorKey="size" header="Size" width="110px" />
					<Table.AvatarColumn accessorKey="uploadedBy" header="Uploaded by" width="200px" />
					<Table.TextColumn accessorKey="when" header="When" width="190px" />
					<Table.ButtonColumn
						header="Download"
						hideHeader
						width="140px"
						button={(row: AttachmentRow) => ({
							label: 'Download',
							icon: 'download',
							onClick: () => saveFile(row.downloadUrl, row.filename),
						})}
					/>
				</Table>
			)}
		</Section>
	);
}
